// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Button,
  Checkbox,
  Grid,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type {
  Bundle,
  BundleEntry,
  Coding,
  Condition,
  Coverage,
  Encounter,
  Patient,
  Practitioner,
  Provenance,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { CodingInput, useMedplum, useMedplumProfile } from '@medplum/react';
import {
  CLOUDPAT_HSAT_CODING,
  CPT_SYSTEM,
  HSAT_CPT_CODE,
  HSAT_PATIENT_APPROVAL_PENDING_TAG,
  PATIENT_TRIAGE_STATUS,
  replaceTriageStatusTags,
} from '@polarissleep/shared';
import { IconRefresh, IconX } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { botIdentifier } from '../../lib/bot-identifier';
import { ICD10_VALUESET } from '../../utils/icd10';
import { coverageToPsgData, createPsgOrderDocument, IN_LAB_PSG_SERVICE_TEXT } from '../../utils/inlab-psg-order-pdf';
import { withTenantScope } from '../../utils/tenant';

const ICD10_CONDITION_VALUESET = ICD10_VALUESET;
const CLINICAL_RATIONALE_OPTIONS = ['STOP-BANG > 3', 'Epworth ≥ 10'];
const SUPPORTING_INDICATIONS_OPTIONS = [
  'Snoring (R06.83)',
  'Witnessed apnea (R06.81)',
  'Somnolence (R40.0)',
  'Hypersomnia, unspecified (G47.10)',
  'Other hypersomnia (G47.19)',
  'Sleep disorder, unspecified (G47.9)',
  'Insomnia, unspecified (G47.00)',
  'Other sleep disorders (G47.8)',
  'Shortness of breath (R06.02)',
  'Hypoxemia (R09.02)',
  'Fatigue (R53.83)',
  'Essential hypertension (I10)',
  'Obesity (E66.9)',
  'Morbid obesity (E66.01)',
  'Type 2 diabetes (E11.9)',
  'Other: Specify',
];
const TEST_TYPE_OPTIONS = [
  { value: 'hsat', label: 'HSAT (WatchPAT)' },
  { value: 'hsat-other', label: 'HSAT (Other)' },
  { value: 'in-lab-psg', label: 'In-Lab (PSG)' },
];
const PRIORITY_OPTIONS = ['Routine', 'Urgent'];

function Tag({ label, onRemove }: { label: string; onRemove: () => void }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        backgroundColor: 'var(--polaris-light-purple)',
        borderRadius: 4,
        padding: '4px 8px 4px 4px',
        fontSize: 14,
      }}
    >
      {label}
      <span
        style={{ cursor: 'pointer', color: '#868e96', lineHeight: 1 }}
        onClick={onRemove}
        role="button"
        aria-label={`Remove ${label}`}
      >
        <IconX size={13} />
      </span>
    </span>
  );
}

interface OrderNewTestModalProps {
  opened: boolean;
  onClose: () => void;
  encounter: Encounter;
  patient: Patient;
  practitioner: Practitioner | undefined;
  existingOrder?: ServiceRequest;
}

export function OrderNewTestModal({
  opened,
  onClose,
  encounter,
  patient,
  practitioner,
  existingOrder,
}: OrderNewTestModalProps): JSX.Element {
  const medplum = useMedplum();
  const signedInPractitioner = useMedplumProfile() as Practitioner | undefined;
  // The encounter practitioner may be a rendering provider selected for billing;
  // the order signature must identify the currently authenticated practitioner.
  const signingPractitioner = signedInPractitioner ?? practitioner;

  const providerFirstName = signingPractitioner?.name?.[0]?.given?.[0] ?? '';
  const providerLastName = signingPractitioner?.name?.[0]?.family ?? '';
  const providerEmail = signingPractitioner?.telecom?.find((t) => t.system === 'email')?.value ?? '';
  const providerPhone = signingPractitioner?.telecom?.find((t) => t.system === 'phone')?.value ?? '';
  const providerNPI = signingPractitioner?.identifier?.find((id) => id.system?.includes('npi'))?.value ?? '';
  const providerFullName = `${providerFirstName} ${providerLastName}`.trim();

  const [localPatient, setLocalPatient] = useState<Patient>(patient);
  const [isReloadingPatient, setIsReloadingPatient] = useState(false);

  useEffect(() => {
    setLocalPatient(patient);
  }, [patient]);

  const patientFirstName = localPatient?.name?.[0]?.given?.[0] ?? '';
  const patientLastName = localPatient?.name?.[0]?.family ?? '';
  const patientEmail = localPatient?.telecom?.find((t) => t.system === 'email')?.value ?? '';
  const patientPhone = localPatient?.telecom?.find((t) => t.system === 'phone')?.value ?? '';
  const patientDOB = localPatient?.birthDate ?? '';
  const patientAddr = localPatient?.address?.[0];
  const patientAddress = {
    line1: patientAddr?.line?.[0] ?? '',
    line2: patientAddr?.line?.[1] ?? '',
    city: patientAddr?.city ?? '',
    state: patientAddr?.state ?? '',
    zip: patientAddr?.postalCode ?? '',
  };

  const reloadPatient = async (): Promise<void> => {
    if (!patient?.id) {
      return;
    }
    setIsReloadingPatient(true);
    try {
      const fresh = await medplum.readResource('Patient', patient.id);
      setLocalPatient(fresh);
    } catch (err) {
      showNotification({ color: 'red', title: 'Error', message: normalizeErrorString(err) });
    } finally {
      setIsReloadingPatient(false);
    }
  };

  const [suspectedDiagnosis, setSuspectedDiagnosis] = useState<Coding | undefined>(undefined);
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState<Coding | null>(null);
  const [diagnosesLoaded, setDiagnosesLoaded] = useState(false);

  useEffect(() => {
    if (!opened) {
      return;
    }
    const principalRef = encounter.diagnosis?.find((d) => d.use?.coding?.[0]?.code === 'principal')?.condition
      ?.reference;
    const secondaryRef = encounter.diagnosis?.find((d) => d.use?.coding?.[0]?.code === 'secondary')?.condition
      ?.reference;

    if (!principalRef && !secondaryRef) {
      setDiagnosesLoaded(true);
      return;
    }

    const fetchDiagnoses = async (): Promise<void> => {
      const [principal, secondary] = await Promise.all([
        principalRef ? medplum.readReference<Condition>({ reference: principalRef }) : undefined,
        secondaryRef ? medplum.readReference<Condition>({ reference: secondaryRef }) : undefined,
      ]);
      if (principal?.code?.coding?.[0]) {
        setSuspectedDiagnosis(principal.code.coding[0]);
      }
      if (secondary?.code?.coding?.[0]) {
        setSecondaryDiagnosis(secondary.code.coding[0]);
      }
      setDiagnosesLoaded(true);
    };

    fetchDiagnoses().catch(console.error);
  }, [opened, encounter, medplum]);
  const [errors, setErrors] = useState<{
    suspectedDiagnosis?: string;
    clinicalRationale?: string;
    supportingIndications?: string;
    signature?: string;
    otherVendor?: string;
  }>({});
  const [clinicalRationaleValues, setClinicalRationaleValues] = useState<string[]>([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState('');
  const [supportingIndicationsTags, setSupportingIndicationsTags] = useState<string[]>([]);
  const [showOtherIndicationInput, setShowOtherIndicationInput] = useState(false);
  const [otherIndicationValue, setOtherIndicationValue] = useState('');
  const [notes, setNotes] = useState('');
  const [testType, setTestType] = useState('hsat');
  const [otherVendor, setOtherVendor] = useState('');
  const [priority, setPriority] = useState('Routine');

  const [signatureOpened, { open: openSignature, close: closeSignature }] = useDisclosure(false);
  const [signatureChecked, setSignatureChecked] = useState(false);
  const [signatureValue, setSignatureValue] = useState(providerFullName);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!opened || !existingOrder) {
      return;
    }
    const notesText =
      existingOrder.note
        ?.map((note) => note.text)
        .filter(Boolean)
        .join('\n') ?? '';
    const rationale = notesText.match(/Clinical Rationale: ([^\n]+)/)?.[1];
    const indications = notesText.match(/Supporting Indications: ([^\n]+)/)?.[1];
    const vendor = notesText.match(/HSAT Vendor: ([^\n]+)/)?.[1];
    const signature = notesText.match(/Signature: ([^\n]+)/)?.[1];
    const codeText = existingOrder.code?.text ?? '';
    setSuspectedDiagnosis(existingOrder.reasonCode?.[0]?.coding?.[0]);
    setSecondaryDiagnosis(existingOrder.reasonCode?.[1]?.coding?.[0] ?? null);
    setClinicalRationaleValues(rationale ? rationale.split(', ') : []);
    setSupportingIndicationsTags(indications ? indications.split(', ') : []);
    setNotes(notesText.match(/Notes: ([^\n]+)/)?.[1] ?? '');
    setOtherVendor(vendor ?? '');
    if (codeText.includes('In-Lab')) {
      setTestType('in-lab-psg');
    } else if (vendor) {
      setTestType('hsat-other');
    } else {
      setTestType('hsat');
    }
    setSignatureValue(signature ?? providerFullName);
  }, [existingOrder, opened, providerFullName]);

  const resetForm = (): void => {
    setSuspectedDiagnosis(undefined);
    setSecondaryDiagnosis(null);
    setDiagnosesLoaded(false);
    setErrors({});
    setClinicalRationaleValues([]);
    setShowOtherInput(false);
    setOtherInputValue('');
    setSupportingIndicationsTags([]);
    setShowOtherIndicationInput(false);
    setOtherIndicationValue('');
    setNotes('');
    setTestType('hsat');
    setOtherVendor('');
    setPriority('Routine');
    setSignatureChecked(false);
    setSignatureValue(providerFullName);
  };

  const handleClose = (): void => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    const newErrors: typeof errors = {};
    if (!suspectedDiagnosis) {
      newErrors.suspectedDiagnosis = 'Suspected Diagnosis is required';
    }
    if (clinicalRationaleValues.length === 0) {
      newErrors.clinicalRationale = 'Clinical Rationale is required';
    }
    if (supportingIndicationsTags.length === 0) {
      newErrors.supportingIndications = 'Supporting Indications is required';
    }
    if (!signatureValue.trim()) {
      newErrors.signature = 'Typed signature is required';
    }
    if (testType === 'hsat-other' && !otherVendor.trim()) {
      newErrors.otherVendor = 'Vendor name is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    if (!patient?.id) {
      return;
    }
    const primaryDiagnosis = suspectedDiagnosis;
    if (!primaryDiagnosis) {
      return;
    }

    setIsSubmitting(true);
    try {
      const isInLabPsg = testType === 'in-lab-psg';
      const isOtherHsat = testType === 'hsat-other';
      const hsatCode = {
        coding: [
          ...(isOtherHsat ? [] : [CLOUDPAT_HSAT_CODING]),
          {
            system: CPT_SYSTEM,
            code: HSAT_CPT_CODE,
            display: 'Home sleep apnea test (HSAT)',
          },
          ...(!isOtherHsat
            ? [{ system: 'http://snomed.info/sct', code: '276734008', display: 'Home sleep apnea test (WatchPAT)' }]
            : []),
        ],
        text: isOtherHsat
          ? `Home Sleep Apnea Test (HSAT) - ${otherVendor.trim()}`
          : 'Home Sleep Apnea Test (HSAT) - WatchPAT',
      };
      const serviceRequest: ServiceRequest = {
        resourceType: 'ServiceRequest',
        meta: isInLabPsg ? undefined : { tag: [HSAT_PATIENT_APPROVAL_PENDING_TAG] },
        status: 'active',
        intent: 'order',
        subject: createReference(patient),
        encounter: createReference(encounter),
        requester: signingPractitioner ? createReference(signingPractitioner) : undefined,
        code: isInLabPsg ? { text: IN_LAB_PSG_SERVICE_TEXT } : hsatCode,
        reasonCode: [
          ...(suspectedDiagnosis ? [{ coding: [suspectedDiagnosis], text: suspectedDiagnosis.display }] : []),
          ...(secondaryDiagnosis ? [{ coding: [secondaryDiagnosis], text: secondaryDiagnosis.display }] : []),
        ],
        note: [
          ...(clinicalRationaleValues.length > 0
            ? [{ text: `Clinical Rationale: ${clinicalRationaleValues.join(', ')}` }]
            : []),
          ...(supportingIndicationsTags.length > 0
            ? [{ text: `Supporting Indications: ${supportingIndicationsTags.join(', ')}` }]
            : []),
          ...(notes ? [{ text: `Notes: ${notes}` }] : []),
          { text: `Signature: ${signatureValue.trim()}` },
          ...(isOtherHsat ? [{ text: `HSAT Vendor: ${otherVendor.trim()}` }] : []),
        ].filter((n) => n.text && !n.text.endsWith(': ')),
        occurrenceDateTime: new Date().toISOString(),
      };

      const updatedPatient: Patient = {
        ...localPatient,
        meta: {
          ...localPatient.meta,
          tag: replaceTriageStatusTags(localPatient.meta?.tag ?? [], PATIENT_TRIAGE_STATUS.hsatApprovedNp),
        },
      };

      const response = await medplum.executeBatch({
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [
          {
            resource: withTenantScope(serviceRequest),
            request: existingOrder?.id
              ? { method: 'PUT', url: `ServiceRequest/${existingOrder.id}` }
              : { method: 'POST', url: 'ServiceRequest' },
          },
          {
            resource: updatedPatient,
            request: { method: 'PUT', url: `Patient/${patient.id}` },
          },
        ],
      });

      const createdServiceRequest = response.entry?.find(
        (entry: BundleEntry) => entry.resource?.resourceType === 'ServiceRequest'
      )?.resource as ServiceRequest | undefined;
      if (!createdServiceRequest?.id) {
        throw new Error('HSAT order was created without a ServiceRequest ID');
      }
      if (existingOrder?.id) {
        await medplum.createResource<Provenance>(
          withTenantScope({
            resourceType: 'Provenance',
            target: [{ reference: `ServiceRequest/${existingOrder.id}` }],
            recorded: new Date().toISOString(),
            agent: signingPractitioner ? [{ who: createReference(signingPractitioner) }] : [],
            activity: { coding: [{ system: 'http://polarissleep.com/order-actions', code: 'amend' }] },
          })
        );
      }

      if (isInLabPsg || isOtherHsat) {
        if (!signingPractitioner) {
          throw new Error('An authenticated provider is required for a sleep study order');
        }
        const coverages = (await medplum.searchResources('Coverage', {
          beneficiary: `Patient/${patient.id}`,
          status: 'active',
          _count: '20',
        })) as Coverage[];
        coverages.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        const payerNames = await Promise.all(
          coverages.slice(0, 2).map(async (coverage) => {
            const payer = coverage.payor?.[0];
            if (!payer?.reference) {
              return undefined;
            }
            const resource = await medplum.readReference(payer).catch(() => undefined);
            return resource?.resourceType === 'Organization' ? resource.name : payer.display;
          })
        );
        await createPsgOrderDocument(medplum, localPatient, signingPractitioner, createdServiceRequest, {
          patient: localPatient,
          practitioner: signingPractitioner,
          primaryCoverage: coverageToPsgData(coverages[0], payerNames[0]),
          secondaryCoverage: coverageToPsgData(coverages[1], payerNames[1]),
          priority,
          orderDate: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
          diagnosisCode: primaryDiagnosis.code,
          diagnosis: primaryDiagnosis.display ?? primaryDiagnosis.code ?? '',
          secondaryDiagnosisCode: secondaryDiagnosis?.code,
          secondaryDiagnosis: secondaryDiagnosis?.display ?? secondaryDiagnosis?.code,
          rationale: clinicalRationaleValues.join(', '),
          supportingIndications: supportingIndicationsTags,
          notes,
          signature: signatureValue.trim(),
          signedAt: new Date().toISOString(),
          orderType: isOtherHsat ? 'hsat-other' : 'in-lab-psg',
          hsatVendor: isOtherHsat ? otherVendor.trim() : undefined,
        });
        showNotification({
          color: 'green',
          title: 'Order Placed',
          message: isOtherHsat
            ? 'HSAT Other order and PDF created successfully.'
            : 'In-lab PSG order and PDF created successfully.',
        });
      } else if (!isOtherHsat) {
        await medplum.executeBot(botIdentifier('order-hsat'), {
          patientId: patient.id,
          serviceRequestId: createdServiceRequest.id,
        });
        showNotification({ color: 'green', title: 'Order Placed', message: 'HSAT order submitted successfully.' });
      }
      resetForm();
      onClose();
    } catch (err) {
      showNotification({ color: 'red', title: 'Error', message: normalizeErrorString(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardStyle = {
    background: 'white',
    border: '1px solid #dee2e6',
    borderRadius: 8,
    padding: 20,
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<span style={{ color: '#640040', fontSize: '1.5rem', fontWeight: 700 }}>Order New Test</span>}
      size="2xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap={'sm'} p={'md'}>
        {/* Suspected Diagnosis */}
        <div style={cardStyle}>
          <Stack gap={'md'}>
            <Title order={4}>Suspected Diagnosis</Title>
            <Grid gutter={'lg'}>
              <Grid.Col span={6}>
                <CodingInput
                  key={`suspected-${diagnosesLoaded}`}
                  name="suspectedDiagnosis"
                  path="Condition.code"
                  label="Suspected Diagnosis *"
                  binding={ICD10_CONDITION_VALUESET}
                  defaultValue={suspectedDiagnosis}
                  onChange={(val) => {
                    setSuspectedDiagnosis(val);
                    setErrors((e) => ({ ...e, suspectedDiagnosis: undefined }));
                  }}
                  outcome={
                    errors.suspectedDiagnosis
                      ? {
                          resourceType: 'OperationOutcome',
                          issue: [
                            { severity: 'error', code: 'required', details: { text: errors.suspectedDiagnosis } },
                          ],
                        }
                      : undefined
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <CodingInput
                  key={`secondary-${diagnosesLoaded}`}
                  name="secondaryDiagnosis"
                  path="Condition.code"
                  label="Secondary Suspected Diagnosis"
                  binding={ICD10_CONDITION_VALUESET}
                  defaultValue={secondaryDiagnosis ?? undefined}
                  onChange={(coding) => setSecondaryDiagnosis(coding ?? null)}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Stack gap={4}>
                  <Select
                    label="Clinical Rationale *"
                    placeholder="Select or type to add..."
                    data={CLINICAL_RATIONALE_OPTIONS.filter(
                      (o) => o === 'Other: Specify' || !clinicalRationaleValues.includes(o)
                    )}
                    value={null}
                    onChange={(val) => {
                      if (!val) {
                        return;
                      }
                      if (val === 'Other: Specify') {
                        setShowOtherInput(true);
                      } else if (!clinicalRationaleValues.includes(val)) {
                        setClinicalRationaleValues((prev) => [...prev, val]);
                        setErrors((e) => ({ ...e, clinicalRationale: undefined }));
                      }
                    }}
                    searchable
                    clearable={false}
                    error={errors.clinicalRationale}
                  />
                  {showOtherInput && (
                    <TextInput
                      placeholder="Type and press Enter to add..."
                      value={otherInputValue}
                      onChange={(e) => setOtherInputValue(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = otherInputValue.trim();
                          if (trimmed && !clinicalRationaleValues.includes(trimmed)) {
                            setClinicalRationaleValues((prev) => [...prev, trimmed]);
                          }
                          setOtherInputValue('');
                          setShowOtherInput(false);
                        }
                      }}
                      autoFocus
                    />
                  )}
                  {clinicalRationaleValues.length > 0 && (
                    <Group gap={4} wrap="wrap">
                      {clinicalRationaleValues.map((val) => (
                        <Tag
                          key={val}
                          label={val}
                          onRemove={() => setClinicalRationaleValues((prev) => prev.filter((v) => v !== val))}
                        />
                      ))}
                    </Group>
                  )}
                </Stack>
              </Grid.Col>
              <Grid.Col span={6}>
                <Stack gap={4}>
                  <Select
                    label="Supporting Indications *"
                    placeholder="Select or type to add..."
                    data={SUPPORTING_INDICATIONS_OPTIONS.filter(
                      (o) => o === 'Other: Specify' || !supportingIndicationsTags.includes(o)
                    )}
                    value={null}
                    onChange={(val) => {
                      if (!val) {
                        return;
                      }
                      if (val === 'Other: Specify') {
                        setShowOtherIndicationInput(true);
                      } else if (!supportingIndicationsTags.includes(val)) {
                        setSupportingIndicationsTags((prev) => [...prev, val]);
                        setErrors((e) => ({ ...e, supportingIndications: undefined }));
                      }
                    }}
                    searchable
                    clearable={false}
                    error={errors.supportingIndications}
                  />
                  {showOtherIndicationInput && (
                    <TextInput
                      placeholder="Type and press Enter to add..."
                      value={otherIndicationValue}
                      onChange={(e) => setOtherIndicationValue(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = otherIndicationValue.trim();
                          if (trimmed && !supportingIndicationsTags.includes(trimmed)) {
                            setSupportingIndicationsTags((prev) => [...prev, trimmed]);
                          }
                          setOtherIndicationValue('');
                          setShowOtherIndicationInput(false);
                        }
                      }}
                      autoFocus
                    />
                  )}
                  {supportingIndicationsTags.length > 0 && (
                    <Group gap={4} wrap="wrap">
                      {supportingIndicationsTags.map((tag) => (
                        <Tag
                          key={tag}
                          label={tag}
                          onRemove={() => setSupportingIndicationsTags((prev) => prev.filter((t) => t !== tag))}
                        />
                      ))}
                    </Group>
                  )}
                </Stack>
              </Grid.Col>
              <Grid.Col span={12}>
                <Textarea
                  label="Notes"
                  placeholder="Patient has concerningly elevated pAHIc (3%), want to rule out OSA."
                  value={notes}
                  onChange={(e) => setNotes(e.currentTarget.value)}
                  minRows={3}
                  autosize
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Test Type"
                  data={TEST_TYPE_OPTIONS}
                  value={testType}
                  onChange={(value) => setTestType(value ?? 'hsat')}
                />
                {testType === 'hsat-other' && (
                  <TextInput
                    label="Company Name *"
                    placeholder="Enter HSAT company name"
                    value={otherVendor}
                    onChange={(event) => {
                      setOtherVendor(event.currentTarget.value);
                      setErrors((error) => ({ ...error, otherVendor: undefined }));
                    }}
                    error={errors.otherVendor}
                  />
                )}
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Priority"
                  data={PRIORITY_OPTIONS}
                  value={priority}
                  onChange={(value) => setPriority(value ?? 'Routine')}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </div>

        {/* Provider Information */}
        <div style={cardStyle}>
          <Stack gap={16}>
            <Title order={4}>Provider Information</Title>
            <Grid gutter={16}>
              <Grid.Col span={6}>
                <Group gap={8} align="flex-end" grow>
                  <TextInput label="First Name" value={providerFirstName} disabled />
                  <TextInput label="Last Name" value={providerLastName} disabled />
                </Group>
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Email" value={providerEmail} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Phone" value={providerPhone} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="NPI" value={providerNPI} disabled />
              </Grid.Col>
            </Grid>
          </Stack>
        </div>

        {/* Patient Information */}
        <div style={cardStyle}>
          <Stack gap={16}>
            <Group justify="space-between" align="center">
              <Title order={4}>Patient Information</Title>
              <Group gap={8}>
                {patient?.id && (
                  <Link to={`/Patient/${patient.id}`} target="_blank" style={{ color: '#640040', fontSize: 14 }}>
                    View Profile
                  </Link>
                )}
                <Tooltip label="Reload patient data">
                  <ActionIcon
                    variant="subtle"
                    color="#640040"
                    loading={isReloadingPatient}
                    onClick={() => {
                      reloadPatient().catch(console.error);
                    }}
                    aria-label="Reload patient"
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
            <Grid gutter={16}>
              <Grid.Col span={6}>
                <Group gap={8} align="flex-end" grow>
                  <TextInput label="First Name" value={patientFirstName} disabled />
                  <TextInput label="Last Name" value={patientLastName} disabled />
                </Group>
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Email" value={patientEmail} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Phone Number" value={patientPhone} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Date of Birth" value={patientDOB} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Street Address" value={patientAddress.line1} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Unit 2" value={patientAddress.line2} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="City" value={patientAddress.city} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="State" value={patientAddress.state} disabled />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Zipcode" value={patientAddress.zip} disabled />
              </Grid.Col>
            </Grid>
          </Stack>
        </div>

        {/* Signature + Actions */}
        <Group align="center" gap={16}>
          <div
            style={{
              flex: 1,
              height: 64,
              border: '2px dashed #640040',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#640040',
              fontSize: 14,
              cursor: 'pointer',
            }}
            onClick={openSignature}
          >
            {signatureChecked ? providerFullName || 'Provider' : 'Signature'}
          </div>
          <Button variant="outline" style={{ borderColor: '#640040', color: '#640040' }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#640040', color: 'white' }}
            onClick={() => {
              handleSubmit().catch(console.error);
            }}
            loading={isSubmitting}
            disabled={
              isSubmitting ||
              !signatureChecked ||
              !suspectedDiagnosis ||
              clinicalRationaleValues.length === 0 ||
              supportingIndicationsTags.length === 0 ||
              (testType === 'hsat-other' && !otherVendor.trim())
            }
          >
            Place Order
          </Button>
        </Group>
      </Stack>

      {/* Signature confirmation modal */}
      <Modal
        opened={signatureOpened}
        onClose={closeSignature}
        centered
        withCloseButton={false}
        styles={{
          content: { borderRadius: 8, padding: 0 },
          body: { padding: 0 },
        }}
      >
        <Stack gap={0}>
          <Group px="lg" py="md" align="center" style={{ borderBottom: '1px solid #dee2e6' }}>
            <Text fw={700} size="lg" c="#640040">
              Signature
            </Text>
          </Group>
          <Stack gap="md" p="lg">
            <TextInput
              label="Type your full name"
              value={signatureValue}
              onChange={(event) => {
                setSignatureValue(event.currentTarget.value);
                setSignatureChecked(false);
              }}
              required
            />
            <Checkbox
              label={
                <Text size="sm">
                  I,{' '}
                  <Text span fw={700}>
                    {signatureValue || providerFullName || 'Provider'}
                  </Text>
                  , confirm that the information provided is accurate and I authorize this order.
                </Text>
              }
              checked={signatureChecked}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignatureChecked(e.currentTarget.checked)}
              color="#640040"
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                style={{ borderColor: '#640040', color: '#640040' }}
                onClick={() => {
                  setSignatureChecked(false);
                  closeSignature();
                }}
              >
                Cancel
              </Button>
              <Button
                style={{ backgroundColor: '#640040', color: 'white' }}
                onClick={closeSignature}
                disabled={!signatureChecked || !signatureValue.trim()}
              >
                Confirm
              </Button>
            </Group>
          </Stack>
        </Stack>
      </Modal>
    </Modal>
  );
}
